import React, { useState, useEffect } from 'react';
// context
import { useConfigSelectors } from 'src/app/reducers/configReducer';
import { useLanguageSelectors } from 'src/app/reducers/languageReducer';
import { useInfringementDataTable } from 'src/@infringements/reducers';
// common components
import { Filter } from 'src/componentsMigrated';
import {
  FilterRow,
  RangeSelector,
  IDropdownItem,
  isCompletedTimeFormat,
  validateTimeFormat,
  isValidTime,
  onCloseTimeDialogValidation,
} from '@ems/client-design-system';
// store
import { filterStore } from 'src/@infringementsCandidates/stores/filterStore';
// function
import { translateLabels } from 'src/@infringementsCandidates/functions';
import { capitalizeObjectKeys } from 'src/utils/objectModifiers';
// interfaces
import { ITableFilterItem } from 'src/@infringementsCandidates/interfaces';
// constants
import { NULL_VALUE } from 'src/constants';
import { useInfringementRulesSelectors } from 'src/app/reducers';
import { InfringementTableDataActionType } from 'src/@infringements/reducers/tableData';
import { InfringementSummaryFilter } from 'src/app/gql/__generated__/hooks';
import { convertSecondsToTimeString, convertTimeStringToSeconds } from 'src/utils';
import { Tooltip2 } from '@blueprintjs/popover2';
import { Position } from '@blueprintjs/core';

export const FilterContainer = ({
  candidateFlag,
  setCandidateFlag,
}: {
  candidateFlag: boolean;
  setCandidateFlag: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    state: {
      hasCandidatesEnabled,
      variables: { filter: infringementFilterState },
    },
    dispatch: infringementFilterDispatch,
  } = useInfringementDataTable();

  // Technically InputTimePeriod type, but using a simple object for error prevention
  const [tempTimeRange, setTempTimeRange] = useState<{ start: string; end: string }>(
    infringementFilterState.timePeriod
      ? {
          start: convertSecondsToTimeString(infringementFilterState.timePeriod?.start || 0),
          end: convertSecondsToTimeString(infringementFilterState.timePeriod?.end || 0),
        }
      : undefined
  );
  const [timeLabel, setTimeLabel] = useState<string>();

  const handleSetTimeRange = (timeRange: { start: string; end: string }) => {
    setTempTimeRange(timeRange);
  };

  // Configuration
  const configSelectors = useConfigSelectors();
  const {
    infringementsCandidates: {
      filter: { availableCategoryList },
    },
  } = configSelectors.getConfig();
  const nullable: any = [];
  const showSelectedIcons: any = [];
  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    components: {
      tooltip: { enableCandidates },
      labels: {
        filters: { clear: clearValue, filter: filterValue, clearFilters },
      },
      filters: { time: languageData },
      hints: { noMatchesFound },
      lists: {
        infringementFilterCategories,
        aircraftCategories,
        candidates,
        ruleTypes,
        severities,
        extraFilterValues,
        infringementStatuses,
      },
    },
  } = languageSelectors.getLanguage();

  const dataListUpdated = capitalizeObjectKeys({
    ...aircraftCategories,
    ...candidates,
    ...extraFilterValues,
    ...ruleTypes,
    ...severities,
    ...infringementStatuses,
  });

  const infringementRulesSelector = useInfringementRulesSelectors();
  const infringementRulesList: IDropdownItem[] = infringementRulesSelector
    .getRules()
    .map(({ name }) => ({ key: name, label: name }));

  const NullValue = { key: NULL_VALUE, label: NULL_VALUE, icon: undefined };
  const filterItems = filterStore.getAllFilterOptions();
  const filterComponents: JSX.Element[] = [];

  const clearAllFilters = () => {
    infringementFilterDispatch({ type: InfringementTableDataActionType.CLEAR_FILTERS });
  };

  (availableCategoryList || []).map((category: string) => {
    if (category === 'ruleNames') {
      const transformedItems =
        nullable && nullable.includes(category)
          ? [NullValue, ...infringementRulesList]
          : infringementRulesList;
      filterComponents.push(
        <Filter
          key={category}
          categoryName={infringementFilterCategories[category]}
          filterItems={translateLabels(transformedItems, dataListUpdated)}
          selectedItems={(infringementFilterState.ruleNames || []).map(item => ({
            key: item,
            label: item,
          }))}
          updateItems={(items: ITableFilterItem[]) => {
            infringementFilterDispatch({
              type: InfringementTableDataActionType.UPDATE_FILTER,
              payload: { filter: 'ruleNames', value: items.map(item => item.key) },
            });
          }}
          iconCategories={showSelectedIcons.map(
            (category: string) => infringementFilterCategories[category]
          )}
          languageData={{ clearValue, filterValue, noMatchesFound }}
        />
      );
    } else if (category === 'candidates') {
      // isInfringement is boolean filter labled 'candidates'
      const originalItems = filterItems[category];
      if (originalItems !== undefined && originalItems.length > 0) {
        const transformedItems =
          nullable && nullable.includes(category) ? [NullValue, ...originalItems] : originalItems;
        const selectedItems =
          infringementFilterState.isInfringement !== undefined
            ? [
                {
                  key: infringementFilterState.isInfringement ? 'Infringement' : 'Candidate',
                  label: infringementFilterState.isInfringement ? 'Infringement' : 'Candidate',
                },
              ]
            : [];
        filterComponents.push(
          <Tooltip2
            key={category}
            disabled={hasCandidatesEnabled}
            content={<p style={{ fontWeight: 'bold', fontSize: '12px' }}>{enableCandidates}</p>}
            position={Position.TOP}>
            <Filter
              disabled={!hasCandidatesEnabled}
              type="single"
              categoryName={infringementFilterCategories[category]}
              filterItems={translateLabels(transformedItems, dataListUpdated)}
              selectedItems={selectedItems}
              updateItems={(items: ITableFilterItem[]) => {
                const booleanValue =
                  items.length === 0
                    ? null
                    : items.find(i => i.key === 'Infringement')
                    ? true
                    : false;
                infringementFilterDispatch({
                  type: InfringementTableDataActionType.UPDATE_BOOLEAN_FILTER,
                  payload: {
                    filter: 'isInfringement',
                    value: booleanValue,
                  },
                });
              }}
              iconCategories={showSelectedIcons.map(
                (category: string) => infringementFilterCategories[category]
              )}
              languageData={{ clearValue, filterValue, noMatchesFound }}
            />
          </Tooltip2>
        );
      }
    } else {
      const originalItems = filterItems[category];
      if (originalItems !== undefined && originalItems.length > 0) {
        const transformedItems =
          nullable && nullable.includes(category) ? [NullValue, ...originalItems] : originalItems;
        filterComponents.push(
          <Filter
            key={category}
            categoryName={infringementFilterCategories[category]}
            filterItems={translateLabels(transformedItems, dataListUpdated)}
            selectedItems={(infringementFilterState[category] || []).map(item => ({
              key: item,
              label: item,
            }))}
            updateItems={(items: ITableFilterItem[]) => {
              infringementFilterDispatch({
                type: InfringementTableDataActionType.UPDATE_FILTER,
                payload: {
                  filter: category as keyof InfringementSummaryFilter,
                  value: items.map(item => item.key),
                },
              });
            }}
            iconCategories={showSelectedIcons.map(
              (category: string) => infringementFilterCategories[category]
            )}
            languageData={{ clearValue, filterValue, noMatchesFound }}
          />
        );
      }
    }
  });

  useEffect(() => {
    const startComplete = tempTimeRange?.start && isCompletedTimeFormat(tempTimeRange.start);
    const endComplete = tempTimeRange?.end && isCompletedTimeFormat(tempTimeRange.end);

    if (startComplete && endComplete) {
      setTimeLabel(`${tempTimeRange.start}-${tempTimeRange.end}`);
      infringementFilterDispatch({
        type: InfringementTableDataActionType.UPDATE_TIME_RANGE_FILTER,
        payload: {
          start: convertTimeStringToSeconds(tempTimeRange.start),
          end: convertTimeStringToSeconds(tempTimeRange.end),
        },
      });
    } else if (tempTimeRange?.start === '' && tempTimeRange?.end === '') {
      setTimeLabel('');
      infringementFilterDispatch({
        type: InfringementTableDataActionType.UPDATE_TIME_RANGE_FILTER,
        payload: { start: undefined, end: undefined },
      });
    }
  }, [tempTimeRange]);

  filterComponents.splice(
    1,
    0,
    <RangeSelector
      key="filter-time-selector"
      type="time"
      min={null}
      max={null}
      fromInputValue={tempTimeRange?.start}
      toInputValue={tempTimeRange?.end}
      setInputValue={(value: string, field: 'from' | 'to') => {
        handleSetTimeRange({
          start: field === 'from' ? value : tempTimeRange?.start,
          end: field === 'to' ? value : tempTimeRange?.end,
        });
      }}
      label={timeLabel}
      validateInputFormat={validateTimeFormat}
      isValidValue={isValidTime}
      onCloseValidation={onCloseTimeDialogValidation}
      isCompletedValueFormat={isCompletedTimeFormat}
      languageData={languageData}
    />
  );

  return (
    <div>
      <FilterRow
        filters={filterComponents}
        clearFilters={clearAllFilters}
        clearDisabled={!Object.keys(infringementFilterState).length}
        languageData={{ clearFilters }}
      />
    </div>
  );
};
